import axios from "axios";
import { auth } from "@/util/firebase";

export interface ErrorLogPayload {
  message: string;
  stack?: string;
  component_stack?: string;
  url: string;
  user_agent: string;
  timestamp: string;
  error_type: "unhandled" | "api" | "component" | "promise";
  context?: Record<string, unknown>;
}

// Queue for batching errors
let errorQueue: ErrorLogPayload[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 10;

// Rate limiting
let errorCount = 0;
let rateLimitResetTime = Date.now();
const MAX_ERRORS_PER_MINUTE = 20;

const enableConsoleLogs =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_BROWSER_CONSOLE_LOGS === "true";
const enableErrorLogging = import.meta.env.VITE_ENABLE_ERROR_LOGGING === "true";

function isRateLimited(): boolean {
  const now = Date.now();
  if (now - rateLimitResetTime > 60000) {
    errorCount = 0;
    rateLimitResetTime = now;
  }
  return errorCount >= MAX_ERRORS_PER_MINUTE;
}

async function flushErrors() {
  if (errorQueue.length === 0) return;

  const errorsToSend = [...errorQueue];
  errorQueue = [];
  flushTimeout = null;

  try {
    // Fire and forget - don't await in production
    await axios.post("/v2/errors/log", { errors: errorsToSend }, {
      // Don't trigger error handler for this request
      validateStatus: () => true,
      timeout: 5000,
    });
  } catch {
    // Silently fail - we don't want error logging to cause more errors
    if (enableConsoleLogs) {
      console.warn("[ErrorLogger] Failed to send errors to server");
    }
  }
}

function scheduleFlush() {
  if (flushTimeout) return;
  flushTimeout = setTimeout(flushErrors, FLUSH_INTERVAL_MS);
}

function queueError(payload: ErrorLogPayload) {
  if (isRateLimited()) {
    if (enableConsoleLogs) {
      console.warn("[ErrorLogger] Rate limited, skipping error");
    }
    return;
  }

  errorCount++;
  errorQueue.push(payload);

  // Flush immediately if queue is full
  if (errorQueue.length >= MAX_QUEUE_SIZE) {
    flushErrors();
  } else {
    scheduleFlush();
  }
}

export function logError(
  error: Error | unknown,
  errorType: ErrorLogPayload["error_type"],
  context?: Record<string, unknown>
): void {
  // Always log to console in dev
  if (enableConsoleLogs) {
    console.error(`[${errorType}]`, error, context);
  }

  // Skip logging in development unless explicitly enabled
  if (import.meta.env.DEV && !enableErrorLogging) {
    return;
  }

  const errorObj = error instanceof Error ? error : new Error(String(error));

  const payload: ErrorLogPayload = {
    message: errorObj.message,
    stack: errorObj.stack,
    url: window.location.href,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    error_type: errorType,
    context: {
      ...context,
      user_id: auth.currentUser?.uid,
    },
  };

  queueError(payload);
}

export function logComponentError(
  error: Error,
  componentStack?: string,
  componentName?: string
): void {
  logError(error, "component", {
    component_stack: componentStack,
    component_name: componentName,
  });
}

export function logApiError(
  error: unknown,
  endpoint?: string,
  method?: string,
  status?: number
): void {
  logError(error, "api", {
    endpoint,
    method,
    status,
  });
}

export function logUnhandledError(error: Error | unknown): void {
  logError(error, "unhandled");
}

export function logPromiseRejection(reason: unknown): void {
  logError(reason, "promise");
}

// Global error handlers - call this once at app startup
export function initGlobalErrorHandlers(): void {
  // Unhandled errors
  window.addEventListener("error", (event) => {
    logUnhandledError(event.error || new Error(event.message));
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    logPromiseRejection(event.reason);
  });

  // Flush errors before page unload
  window.addEventListener("beforeunload", () => {
    if (errorQueue.length > 0) {
      // Use sendBeacon for reliability during page unload
      const payload = JSON.stringify({ errors: errorQueue });
      const baseUrl = import.meta.env.VITE_API_V2_BASE_URL || "";
      navigator.sendBeacon(`${baseUrl}/v2/errors/log`, payload);
    }
  });

  if (enableConsoleLogs) {
    console.log("[ErrorLogger] Global error handlers initialized");
  }
}
